import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { lcCoachingApi } from '@/api/lcCoaching'
import type { LcTestDetailView, TestSelection } from '@/types/coaching'
import { coachingKeys } from './useCoachingToday'
import { gradingDraftScope, useClearGradingDraftRow } from './useGradingDraft'

/** 학습자의 전 테스트 요약 + 챕터/누적 평균 — 트리 머지·메트릭용. */
export function useTests(learnerId: number) {
  return useQuery({
    queryKey: coachingKeys.tests(learnerId),
    queryFn: () => lcCoachingApi.listTests(learnerId),
    enabled: Number.isFinite(learnerId) && learnerId > 0,
    staleTime: 30_000,
  })
}

/** 채점 영역 detail(문제/정답/해설 + 현재 채점). testId 미정 시 비활성. */
export function useTestDetail(learnerId: number, testId: number | undefined) {
  return useQuery({
    queryKey: coachingKeys.testDetail(learnerId, testId ?? 0),
    queryFn: () => lcCoachingApi.getTestDetail(learnerId, testId as number),
    enabled: Number.isFinite(learnerId) && learnerId > 0 && !!testId,
    staleTime: 10_000,
  })
}

/** 출제(멱등 generate-or-get) — 트리 노드 선택 시 testId 확보. staleTime=Infinity 로 재-POST 방지. */
export function useEnsureTest(learnerId: number, selection: TestSelection | null, { enabled = true }: { enabled?: boolean } = {}) {
  const forcePrerequisite = selection?.forcePrerequisite === true
  return useQuery({
    queryKey: selection
      ? coachingKeys.testEnsure(
          learnerId,
          selection.kind === 'CHAPTER' ? selection.kind : 'CHAPTER',
          selection.chapterOrdinal,
          selection.scopeContentId,
          forcePrerequisite,
        )
      : (['coaching', 'test-ensure', 'none'] as const),
    queryFn: () => {
      // A disabled read-only observer may still retain an existing cached test.
      // Manual refetch must not bypass that host restriction and create a test.
      if (!enabled || selection?.kind !== 'CHAPTER') throw new Error('Chapter test generation is not enabled')
      return lcCoachingApi.generateTest(learnerId, 'CHAPTER', selection.chapterOrdinal, selection.scopeContentId, forcePrerequisite)
    },
    enabled: enabled && Number.isFinite(learnerId) && learnerId > 0 && selection?.kind === 'CHAPTER',
    staleTime: Infinity,
  })
}

interface GradeTestItemOptions {
  /**
   * 저장 성공마다 테스트 목록(트리 상태·평균)을 재조회한다(기본 true).
   * 채점표는 문항별 저장 후 한 번만 갱신하려고 false를 쓴다.
   */
  refreshTests?: boolean
}

/** 문항 답안/수동 정오답 채점. 답안만 보내면 서버가 자동채점한다. */
export function useGradeTestItem(
  learnerId: number,
  testId: number,
  options: GradeTestItemOptions = {},
) {
  const { refreshTests = true } = options
  const qc = useQueryClient()
  const key = coachingKeys.testDetail(learnerId, testId)
  const clearDraftRow = useClearGradingDraftRow()
  return useMutation({
    mutationFn: (v: { quizId: string; isCorrect?: boolean; answer?: string; manualGrade?: boolean }) =>
      lcCoachingApi.gradeTestItem(
        learnerId,
        testId,
        v.quizId,
        v.isCorrect,
        v.answer,
        v.manualGrade,
      ),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<LcTestDetailView>(key)
      if (prev) {
        qc.setQueryData<LcTestDetailView>(key, {
          ...prev,
          items: prev.items.map((it) =>
            it.quizId === v.quizId
              ? {
                  ...it,
                  answer: v.answer ?? it.answer,
                  gradedIsCorrect: v.isCorrect ?? it.gradedIsCorrect,
                }
              : it,
          ),
        })
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev)
    },
    onSuccess: (detail, variables) => {
      if (variables.answer != null) clearDraftRow(gradingDraftScope.test(testId), variables.quizId)
      qc.setQueryData(key, detail)
      // 점수/상태 변화는 트리 상태·평균에도 반영.
      if (refreshTests) qc.invalidateQueries({ queryKey: coachingKeys.tests(learnerId) })
    },
  })
}

/** [채점 완료] — score 확정 + COMPLETED. */
export function useCompleteTest(learnerId: number, testId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => lcCoachingApi.completeTest(learnerId, testId),
    onSuccess: (detail) => {
      qc.setQueryData(coachingKeys.testDetail(learnerId, testId), detail)
      qc.invalidateQueries({ queryKey: coachingKeys.tests(learnerId) })
    },
  })
}

/** 완료 테스트의 새 회차를 생성하거나 이미 열린 미완료 회차를 멱등 반환한다. */
export function useRetakeTest(learnerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (testId: number) => lcCoachingApi.retakeTest(learnerId, testId),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: coachingKeys.tests(learnerId) })
      qc.invalidateQueries({ queryKey: coachingKeys.testDetail(learnerId, result.testId) })
    },
  })
}

// ── Phase 5c — 리포트 + 복습추출 ──

/** 리포트(문항별 정오답/단원/해설 + 점수). A4 리포트 페이지용. */
export function useTestReport(learnerId: number, testId: number) {
  return useQuery({
    queryKey: ['coaching', 'learner', learnerId, 'test', testId, 'report'] as const,
    queryFn: () => lcCoachingApi.getTestReport(learnerId, testId),
    enabled: Number.isFinite(learnerId) && learnerId > 0 && testId > 0,
    staleTime: 10_000,
  })
}

/** 복습추출 후보(기준 정답률 미만 레슨). threshold 변경 시 재조회. */
export function useReviewCandidates(learnerId: number, testId: number, threshold: number) {
  return useQuery({
    queryKey: ['coaching', 'learner', learnerId, 'test', testId, 'review-candidates', threshold] as const,
    queryFn: () => lcCoachingApi.getReviewCandidates(learnerId, testId, threshold),
    enabled: Number.isFinite(learnerId) && learnerId > 0 && testId > 0,
    staleTime: 10_000,
  })
}
